import { DailyCheckInStayApi, DailyCheckInRoomAllocationApi } from "../../services/accommodationService";

export type AccommodationStay = {
  venue: string;
  property: string;
  fromDate: string;
  toDate: string;
  rooms?: { roomNumber?: string | number | null; roomTypeName?: string | null }[];
};

export const getApplicantAge = (
  birthDate: string | null,
  arrivalDate: string,
) => {
  if (!birthDate) return null;
  const birth = birthDate.slice(0, 10);
  const arrival = arrivalDate.slice(0, 10);
  if (Number.isNaN(Date.parse(birth)) || Number.isNaN(Date.parse(arrival)))
    return null;
  const age =
    Number(arrival.slice(0, 4)) -
    Number(birth.slice(0, 4)) -
    (arrival.slice(5) < birth.slice(5) ? 1 : 0);
  return age >= 0 ? age : null;
};

export const getStayDateSortValue = (value: string) => {
  const dateParts = value.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (dateParts) {
    return Date.UTC(
      Number(dateParts[3]),
      Number(dateParts[2]) - 1,
      Number(dateParts[1]),
      Number(dateParts[4] ?? 0),
      Number(dateParts[5] ?? 0),
      Number(dateParts[6] ?? 0),
    );
  }

  const parsedDate = Date.parse(value);
  return Number.isNaN(parsedDate) ? Number.MAX_SAFE_INTEGER : parsedDate;
};

export const sortAccommodationStays = (stays: AccommodationStay[]) =>
  [...stays].sort(
    (firstStay, secondStay) =>
      getStayDateSortValue(firstStay.fromDate) -
      getStayDateSortValue(secondStay.fromDate),
  );

export const mapReportStays = (
  stays: DailyCheckInStayApi[],
): AccommodationStay[] =>
  sortAccommodationStays(
    stays.map((stay) => {
      const formatStayDate = (date: string, time: string | null) => {
        const [year, month, day] = date.slice(0, 10).split("-");
        return `${day}/${month}/${year}${time ? ` ${time}` : ""}`;
      };
      return {
        venue: stay.venueName,
        property: stay.propertyName,
        fromDate: formatStayDate(stay.startDate, stay.checkInTime),
        toDate: formatStayDate(stay.endDate, stay.checkOutTime),
      };
    }),
  );

export const addApplicantRoomsToStays = (
  stays: AccommodationStay[],
  allocations: DailyCheckInRoomAllocationApi[],
  visaApplicationId: number,
): AccommodationStay[] => {
  const normalize = (value: string) => value.trim().toLowerCase();
  return stays.map((stay) => ({
    ...stay,
    rooms: allocations.filter((allocation) =>
      allocation.isActive !== false &&
      normalize(allocation.venueName) === normalize(stay.venue) &&
      normalize(allocation.propertyName) === normalize(stay.property) &&
      allocation.applicants?.some((applicant) =>
        applicant.isActive !== false && applicant.visaApplicationId === visaApplicationId,
      ) &&
      (allocation.roomNumber != null || allocation.roomTypeName),
    ).map(({ roomNumber, roomTypeName }) => ({ roomNumber, roomTypeName })),
  }));
};
